import { type FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Image from '@segment/matcha/components/Image'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import dataCollectionPng2x from './assets/DataCollection@2x.png'
import dataCollectionWebp from './assets/DataCollection.webp'
import dataCollectionWebp2x from './assets/DataCollection@2x.webp'

const copy = {
  heading: `Data collection & ETL`,
  description: `Collect data from every source with a single API and get it where it needs to be—fast.`,
}

const data = [
  {
    heading: `Unblock data silos effortlessly`,
    description: `Quickly connect all of your data sources and send clean, consented data to any of your downstream tools in real-time.`,
  },
  {
    heading: `Integration without limitations`,
    description: `Ease data unification with 450+ ready-to-use integrations, reverse ETL for direct syncing from the data warehouse, and Functions to create custom sources and destinations using Javascript and AI.`,
  },
  {
    heading: `Open, flexible, scalable infrastructure`,
    description: `Choose the best tools for your data stack without worrying about whether your CDP supports it. Additionally, our Developer Toolkit lets you customize and extend Segment to fit all of your unique requirements.`,
  },
]

const button = {
  href: `/product/connections/`,
  text: `Connections`,
}

const DataCollection: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper id="data-collection-etl">
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pLarge" tag="p">
            {copy.description}
          </CopyText.Description>
        </Section.Copy>

        <Section.Contents>
          <ColumnsWrapper>
            <ColumnTrack columns={1} overrides={{ md: 2 }}>
              <picture>
                <source type="image/webp" srcSet={`${dataCollectionWebp}, ${dataCollectionWebp2x} 2x`} />
                <StyledImage
                  width="877"
                  height="824"
                  src={dataCollectionPng2x}
                  alt="Illustration: Data Collection"
                  loading="lazy"
                />
              </picture>

              <Content>
                {data.map(({ heading, description }) => (
                  <FeatureBoxBase key={heading} alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{heading}</FeatureBox.Title>
                      <FeatureBox.Text variant="pMedium">{description}</FeatureBox.Text>
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}

                <ButtonWrapper>
                  <ButtonText
                    variant="primary"
                    size="medium"
                    href={getAbsoluteUrl(`${button.href}`, pageConfig)}
                    iconEnd={ChevronRightIcon}
                  >
                    {button.text}
                  </ButtonText>
                </ButtonWrapper>
              </Content>
            </ColumnTrack>
          </ColumnsWrapper>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
  }
`

const ColumnsWrapper = styled.div`
  margin-top: 50px;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-top: 20px;
  }
`

const Content = styled.div`
  max-width: 460px;
  display: grid;
  align-content: center;
  gap: 24px;
  justify-self: center;
`

const StyledImage = styled(Image)`
  width: 100%;
  max-width: 400px;
`

const ButtonWrapper = styled.div``

export default DataCollection
